import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { useAppQuery, useInvalidateQueries } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/index';

/**
 * How one reverse-vested issuance settled at termination. Present only once the repurchase actually
 * ran: a stakeholder terminated before the repurchase workflow existed has no settlement, and the
 * backend sends null for the whole block rather than inventing one.
 */
export type ReverseVestingTermination = {
  /** Unvested shares the admin pulled forward to vest on termination. */
  acceleratedShares: number;
  compensation: number;
  lapsedShares: number;
  repurchasePrice: number;
  terminationDate: string;
  /** Final vested: the contractual position at the Last Vesting Date plus any acceleration. */
  vestedShares: number;
};

export type ReverseVestingRow = {
  /**
   * Schedule shares no longer held — sold, bought back or converted out of the share class. The
   * backend attributes them vested-first, so `vestedShares` + `unvestedShares` are what is held.
   */
  disposedShares: number;
  documents: string[];
  fullVestingDate: string;
  id: string;
  issueDate: string;
  nextVestingDate: null | string;
  /** Shares on the row subject to repurchase — never more than `sharesIssued`. */
  reverseVestingShares: number;
  shareClassName: string;
  sharesIssued: number;
  termination: null | ReverseVestingTermination;
  /** Reverse-vesting shares still held and repurchasable. */
  unvestedShares: number;
  /** Null for company incorporation rows, which have no valuation event of their own. */
  valuationName: null | string;
  vestedShares: number;
};

type UseReverseVestingProps = {
  enabled?: boolean;
  stakeholderId: string;
};

export const useReverseVesting = ({ enabled = true, stakeholderId }: UseReverseVestingProps) => {
  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_STAKEHOLDER_REVERSE_VESTING);
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const company = selectedCompanyStorage(({ company }) => company);
  const isCompanyContextReady = company?.id === companyId;

  const { data: reverseVestingRows = [], isLoading } = useAppQuery({
    queryKey: [QueryKey.GET_STAKEHOLDER_REVERSE_VESTING, companyId, stakeholderId],
    queryFn: () =>
      ApiService.get<ReverseVestingRow[]>({
        endpoint: BackendRoute.COMPANIES,
        routePath: [companyId, 'events', 'stakeholders', stakeholderId, 'reverse-vesting'],
      }),
    enabled: enabled && isCompanyContextReady && Boolean(companyId) && Boolean(stakeholderId),
  });

  return {
    invalidateQuery,
    isLoading,
    reverseVestingRows,
  };
};
